import React from 'react';
import { ThermometerIcon } from 'lucide-react';
import { useAppState } from '../contexts/AppStateContext';
import { DEMO_DATE } from '../data/demo';
import { copy } from '../data/copy';
import { exceptionTypeLabels } from '../data/labels';
import { useTodayPlan } from '../hooks/useTodayPlan';
import { partLabels, partOrder } from '../utils/dayParts';
import { formatDate, formatDayLabel } from '../utils/time';
import { eveningLabel } from '../utils/today';
import { isDoneStatus } from '../utils/progress';
import { TaskRow } from '../components/tasks/TaskRow';
import { NextUpCard } from '../components/NextUpCard';
import { WeekScoreCard } from '../components/today/WeekScoreCard';
import { GoodiesChancesCard } from '../components/today/GoodiesChancesCard';
import { DayPart, DayPartTiming } from '../components/today/DayPart';
import { EventLine } from '../components/today/EventLine';
import { StudyRowItem } from '../components/today/StudyRowItem';
import { TodayMenu } from '../components/today/TodayMenu';
import { DutyCard } from '../components/duties/DutyCard';
export function TodayPage() {
  const {
    family,
    viewer,
    db
  } = useAppState();
  const plan = useTodayPlan();
  const nowIdx = partOrder.indexOf(plan.now);
  const myExceptions = db.exceptions.filter(e => e.familyId === family.id && e.memberId === viewer.id && e.to >= DEMO_DATE && e.status !== 'zavrnjeno');
  const greeting = copy.greetings[viewer.name.length % copy.greetings.length];
  const weeklyDone = plan.weekly.filter(i => isDoneStatus(i.occ.status)).length;
  const goodies = <GoodiesChancesCard offers={plan.offers} shared={plan.shared} roomDay={plan.roomDay} data-id="element-1610" data-mp="wh7rhqsnep5u" />;
  return <div data-id="element-1611" data-mp="16h8djkhwjjdq">
      <header className="flex items-start justify-between gap-4" data-id="element-1612" data-mp="16hi5veqf62ml">
        <div className="min-w-0" data-id="element-1613" data-mp="16f5de7n1c9va">
          <p className="text-sm font-bold capitalize text-muted" data-id="element-1614" data-mp="ire2n8h584w4">{formatDayLabel(DEMO_DATE)}</p>
          <h1 className="font-display text-3xl leading-tight md:text-4xl" data-id="element-1615" data-mp="dsszb06lh53g">
            {greeting}, {viewer.name}!
          </h1>
          <p className="mt-1 text-sm font-semibold text-muted" data-id="element-1616" data-mp="13fnds5mo9rtt">
            {plan.total === 0 ? copy.emptyDay : `${plan.done} od ${plan.total} kljukic${plan.pointsToday > 0 ? ` · danes +${plan.pointsToday} cvenkotočk` : ''}`}
          </p>
        </div>
        <TodayMenu data-id="element-1617" data-mp="1h8mozvff8zvk" />
      </header>

      {myExceptions.map(e => <div key={e.id} className={`mt-4 flex items-center gap-3 rounded-2xl px-4 py-3 text-sm ${e.status === 'odobreno' ? 'bg-excused-soft text-excused-ink' : 'bg-review-soft text-review-ink'}`} data-id="element-1618" data-mp="1h9xtxyklsdo9">
          <ThermometerIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-1619" data-mp="thzfx6trs47f" />
          <p className="min-w-0 flex-1" data-id="element-1620" data-mp="yhlt5g9llkg8">
            <b data-id="element-1621" data-mp="yin3tjmpc4xf">{exceptionTypeLabels[e.type]}</b> {formatDate(e.from)}–{formatDate(e.to)} ·{' '}
            {e.status === 'odobreno' ? 'izjema velja – oproščeno se ne šteje' : 'predlog čaka na starša'}
          </p>
        </div>)}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8" data-id="element-1622" data-mp="29yvd03wn2ei2">
        <div className="min-w-0 space-y-3" data-id="element-1623" data-mp="3ue9t5trfipog">
          <div className="mb-3 lg:hidden" data-id="element-1624" data-mp="3pg01vu8ts984">
            <WeekScoreCard data-id="element-1625" data-mp="3pg8edl11dcis" />
          </div>

          {plan.allDone && <p className="rounded-2xl bg-done-soft px-4 py-3 text-sm font-bold text-done-ink" data-id="element-1626" data-mp="3ko87f8mjtjcp">
              {copy.allDone} Danes +{plan.pointsToday} cvenkotočk.
            </p>}

          {plan.parts.map((p, idx) => {
          const timing: DayPartTiming = idx < nowIdx ? 'past' : idx === nowIdx ? 'now' : 'future';
          const empty = p.tasks.length === 0 && p.study.length === 0 && p.events.length === 0;
          if (empty && timing !== 'now') return null;
          return <DayPart key={p.part} title={partLabels[p.part]} timing={timing} total={p.total} done={p.done} note={p.part === 'vecer' && family.modules.vecerniRezim ? `${eveningLabel(family.rules.evening, DEMO_DATE)} · naprave: ${family.rules.evening.devicesPlace}` : undefined} data-id="element-1627" data-mp="2ws46csuzsncx" data-mpi={idx}>
                {empty && <p className="px-4 py-3 text-sm text-muted" data-id="element-1628" data-mp="17y5n10kkut1k">Za ta del dneva ni nič obveznega.</p>}
                {p.events.length > 0 && <ul className="border-b border-line py-1" data-id="element-1629" data-mp="t1y4e4othzoe">{p.events.map(e => <EventLine key={e.id} event={e} data-id="element-1630" data-mp="y0hqq67bpdha" />)}</ul>}
                {(p.tasks.length > 0 || p.study.length > 0) && <ul className="divide-y divide-line" data-id="element-1631" data-mp="k8bc4ojtjejy">
                    {p.tasks.map(i => <TaskRow key={i.template.id} item={i} data-id="element-1632" data-mp="p25qdcgnn1bn" />)}
                    {p.study.map(s => <StudyRowItem key={s.key} row={s} data-id="element-1633" data-mp="a9vhwcphvp68" />)}
                  </ul>}
              </DayPart>;
        })}

          {(plan.weekly.length > 0 || plan.duties.length > 0) && <DayPart title="Ta teden skrbiš za" timing="future" total={plan.weekly.filter(i => !i.excused && i.relation !== 'predano').length} done={weeklyDone} summary={plan.duties.length > 0 ? `${plan.duties.length} ${plan.duties.length === 1 ? 'zadolžitev' : plan.duties.length === 2 ? 'zadolžitvi' : 'zadolžitve'}` : `${weeklyDone}/${plan.weekly.length}`} note={[...plan.duties.map(t => t.planKey === 'soba' ? 'Moja soba' : t.title), ...plan.weekly.map(i => i.template.title)].join(' · ')} data-id="element-1634" data-mp="5cvd054dssgp">
              {plan.duties.length > 0 && <>
                  <p className="px-4 pt-3 text-xs text-muted" data-id="element-1635" data-mp="m9olymylo9e">Skrb ves teden. Starš pregleda v ponedeljek, sredo in soboto – cvenkotočke po deležu urejenih pregledov.</p>
                  <ul className="divide-y divide-line" data-id="element-1636" data-mp="aepa35xgsuz1">
                    {plan.duties.map(t => <DutyCard key={t.id} template={t} data-id="element-1637" data-mp="odyd13wntsl2" />)}
                  </ul>
                </>}
              {plan.weekly.length > 0 && <ul className="divide-y divide-line border-t border-line" data-id="element-1638" data-mp="3d7v3awoxorsg">
                  {plan.weekly.map(i => <TaskRow key={i.template.id} item={i} data-id="element-1639" data-mp="3h62n60ep297v" />)}
                </ul>}
            </DayPart>}

          <div className="space-y-4 pt-3 lg:hidden" data-id="element-1640" data-mp="3qyi1mnqs8fo0">
            {goodies}
            <NextUpCard data-id="element-1641" data-mp="3vx1rv0cesdm6" />
          </div>
        </div>

        <aside className="hidden space-y-4 lg:block" data-id="element-1642" data-mp="3vwjmne7rqp24">
          <WeekScoreCard data-id="element-1643" data-mp="4qib74jw5tfy" />
          {goodies}
          <NextUpCard data-id="element-1644" data-mp="4p75gz6vybm7" />
        </aside>
      </div>
    </div>;
}